declare global {
  interface Window {
    posthog?: {
      capture: (name: string, properties?: Record<string, unknown>) => void;
      captureException?: (
        error: unknown,
        additionalProperties?: Record<string, unknown>,
      ) => void;
      identify?: (
        distinctId: string,
        properties?: Record<string, unknown>,
      ) => void;
      init?: (key: string, options?: Record<string, unknown>) => void;
      opt_in_capturing?: () => void;
      register?: (properties: Record<string, unknown>) => void;
      register_once?: (properties: Record<string, unknown>) => void;
      set_config?: (options: Record<string, unknown>) => void;
    };
  }
}

let initialized = false;
let consented = false;

function readEnv(key: string) {
  return typeof import.meta !== 'undefined' ? import.meta.env[key] : '';
}

function injectSnippet() {
  return new Promise<void>((resolve, reject) => {
    if (window.posthog) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = `${readEnv('VITE_POSTHOG_HOST') || 'https://us.i.posthog.com'}/static/array.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load PostHog'));
    document.head.appendChild(script);
  });
}

export async function initPosthog() {
  const key = readEnv('VITE_POSTHOG_KEY');
  if (!key || typeof window === 'undefined') return false;
  if (initialized && consented) return true;
  await injectSnippet();
  if (!initialized) {
    window.posthog?.init?.(key, {
      api_host: readEnv('VITE_POSTHOG_HOST') || 'https://us.i.posthog.com',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: true,
      scroll_depth: true,
      enable_exception_autocapture: true,
      persistence: 'localStorage+cookie',
    });
  } else {
    window.posthog?.set_config?.({
      capture_pageleave: true,
      scroll_depth: true,
      enable_exception_autocapture: true,
      persistence: 'localStorage+cookie',
    });
  }
  window.posthog?.opt_in_capturing?.();
  initialized = true;
  consented = true;
  return true;
}

export async function initAnonymousPosthog() {
  const key = readEnv('VITE_POSTHOG_KEY');
  if (!key || typeof window === 'undefined') return false;
  if (consented || initialized) return true;
  await injectSnippet();
  window.posthog?.init?.(key, {
    api_host: readEnv('VITE_POSTHOG_HOST') || 'https://us.i.posthog.com',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    scroll_depth: false,
    enable_exception_autocapture: false,
    persistence: 'memory',
  });
  initialized = true;
  return true;
}

export function capturePosthogEvent(
  name: string,
  properties?: Record<string, unknown>,
) {
  if (!initialized) return;
  window.posthog?.capture(name, properties);
}

export function identifyPosthogUser(
  distinctId: string,
  properties?: Record<string, unknown>,
) {
  if (!initialized || !consented) return;
  window.posthog?.identify?.(distinctId, properties);
}

export function capturePosthogException(
  error: unknown,
  additionalProperties?: Record<string, unknown>,
) {
  if (!initialized || !consented) return;
  window.posthog?.captureException?.(error, additionalProperties);
}

export function registerPosthogProperties(
  properties: Record<string, unknown>,
  once = false,
) {
  if (!initialized) return;
  if (once) {
    window.posthog?.register_once?.(properties);
    return;
  }
  window.posthog?.register?.(properties);
}
